/**
 * 날짜 칸(`DateField.tsx`)의 글자 다루기 — `YYYY-MM-DD`(2026-11-03).
 *
 * 브라우저의 `<input type="date">` 는 브라우저·로캘마다 모양이 다르고("2026. 11. 03."), 좁은 칸(92px)에서는 일이 잘려
 * 년·월만 보였다(무재해 현황의 시작일). 시각 칸(`TimeField`)처럼 글자 칸 + 달력으로 바꿨고, 적은 글자는 여기서 읽는다.
 * 값은 브라우저 날짜 칸과 같은 `YYYY-MM-DD` 라 부르는 쪽은 바뀌지 않는다.
 */

const pad = (value: number): string => String(value).padStart(2, '0');

/** 달력에 있는 날인가(2월 30일 같은 날을 거른다). */
function calendarDate(year: number, month: number, day: number): string | null {
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** `YYYY-MM-DD` 이고 달력에 있는 날인가. */
export function isDateText(value: string): boolean {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return !!parts && calendarDate(Number(parts[1]), Number(parts[2]), Number(parts[3])) !== null;
}

/**
 * 적은 글자를 `YYYY-MM-DD` 로 — "20261103"·"2026-11-3"·"2026.11.03"·"2026/11/3"·"2026년 11월 3일" → 2026-11-03,
 * 해를 빼면 `today` 의 해로("11.3"·"11/3"·"11월 3일"). 달력에 없는 날이거나 못 읽으면 `null` — 부르는 쪽이 앞의 값으로
 * 되돌린다.
 */
export function parseDateText(text: string, today: string): string | null {
  const compact = text.replace(/\s+/g, '').replace(/\.$/, '');
  if (!compact) return null;
  const digits = /^(\d{4})(\d{2})(\d{2})$/.exec(compact);
  if (digits) return calendarDate(Number(digits[1]), Number(digits[2]), Number(digits[3]));
  const full = /^(\d{4})[-./년](\d{1,2})[-./월](\d{1,2})일?$/.exec(compact);
  if (full) return calendarDate(Number(full[1]), Number(full[2]), Number(full[3]));
  const short = /^(\d{1,2})[-./월](\d{1,2})일?$/.exec(compact);
  if (short) return calendarDate(Number(today.slice(0, 4)), Number(short[1]), Number(short[2]));
  return null;
}

/** 날짜를 하루 단위로 옮긴다. 못 읽는 값은 그대로. */
export function stepDate(value: string, days: number): string {
  if (!isDateText(value)) return value;
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** 달을 옮긴다 — 12월 다음은 이듬해 1월. */
export function shiftMonth(view: { year: number; month: number }, delta: number): { year: number; month: number } {
  const index = view.year * 12 + (view.month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/** 그달의 달력 — 일요일부터 6주(42칸). 앞뒤 달의 날은 `inMonth: false`. */
export function monthGrid(year: number, month: number): Array<{ date: string; day: number; inMonth: boolean }> {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const start = Date.UTC(year, month - 1, 1 - first.getUTCDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start + index * 86_400_000);
    return {
      date: `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`,
      day: date.getUTCDate(),
      inMonth: date.getUTCMonth() === month - 1,
    };
  });
}

export const WEEKDAY_LABELS: readonly string[] = ['일', '월', '화', '수', '목', '금', '토'];
